import React from "react";

import { TicketPriority } from "@common/models/TicketPriority.model";
import { TicketStatus } from "@common/models/TicketStatus.model";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { RoutePaths } from "@main/router/config/routePaths";
import { CellContext, ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { generatePath } from "react-router-dom";

import { getTagColor } from "../../Input/InputWithTags/InputWithTags";
import { IssueTableRowItem } from "../../MasterTable/models/MasterTableModel";
import TagComponent from "../../Tag/Tag";
import { BaseCell } from "../ui/BaseCell";
import { HeaderCell } from "../ui/BaseTableStyles";
import { DragHandleCell } from "../ui/DragHandleCell";
import { SelectionHeaderCell } from "../ui/SelectionHeaderCell";
import { SortHeaderCell, TicketSortOption, UserSortOption } from "../ui/SortHeaderCell";
import { SpaceToPreviewIndicator } from "../ui/SpaceToPreviewIndicator";
import { TicketTitleCell } from "../ui/TicketTitleCell";
import { useBaseTable } from "../useBaseTable";

type TableProps = {
  selectable?: boolean;
  data: IssueTableRowItem[];
  hasMore: boolean;
  isLoading: boolean;
  isFetchingNext: boolean;
  total: number;
  loadMore: () => void;
  onDeleteRequest: (rows: IssueTableRowItem[]) => void;
  onMarkAsReadRequest: (rows: IssueTableRowItem[]) => void;
  onBulkChangePriority: (rows: IssueTableRowItem[], priority: TicketPriority, resetSelection?: () => void) => void;
  onBulkChangeStatus: (rows: IssueTableRowItem[], status: TicketStatus, resetSelection?: () => void) => void;
  onBulkChangeAssignee: (rows: IssueTableRowItem[], assigneeId: string | null, resetSelection?: () => void) => void;
  onBulkAddNoteSendChat: (rows: IssueTableRowItem[], message: string, isNote?: boolean) => void;
  onBulkAddTags: (rows: IssueTableRowItem[], tags: string[]) => void;
  onPreviewRequest: (row: IssueTableRowItem) => void;
  onPreviewClose: () => void;
  onChangeStatusRequest: (status: string, ticketID: string) => Promise<void>;
  onChangePriorityRequest: (priority: string, ticketID: string) => Promise<void>;
  onChangeSortOption: (sort: TicketSortOption | UserSortOption) => void;
  selectedSortOption: TicketSortOption | UserSortOption;
  onChangeAssigneeRequest: (assignee_id: string | null, ticketID: string) => Promise<void>;
  currentMonthIssues?: number;
  onMergeAction: (rows: IssueTableRowItem[], row: IssueTableRowItem) => void;
};

export function useFeedbackTable({
  selectable,
  data,
  hasMore,
  isLoading,
  isFetchingNext,
  loadMore,
  onDeleteRequest,
  onMarkAsReadRequest,
  onBulkChangePriority,
  onBulkChangeAssignee,
  onBulkChangeStatus,
  onBulkAddNoteSendChat,
  onBulkAddTags,
  onPreviewRequest,
  onPreviewClose,
  onChangeStatusRequest,
  onChangePriorityRequest,
  onChangeSortOption,
  selectedSortOption,
  onChangeAssigneeRequest,
  total,
  currentMonthIssues,
  onMergeAction,
}: TableProps) {
  const { selectedApp, selectedWorkspace } = useAppSelectionContext();

  const baseTableProps = useBaseTable({
    data,
    columns: useDefaultFeedbackColumns({
      onDeleteRequest,
      onMarkAsReadRequest,
      onBulkChangePriority,
      onBulkChangeStatus,
      onBulkChangeAssignee,
      onBulkAddNoteSendChat,
      onBulkAddTags,
      onChangeStatusRequest,
      onChangePriorityRequest,
      onChangeSortOption,
      selectedSortOption,
      onChangeAssigneeRequest,
      total,
      currentMonthIssues,
      onMergeAction,
    }) as unknown as ColumnDef<IssueTableRowItem>[],
    selectable,
    hasMore,
    isLoading,
    isFetchingNext,
    loadMore,
    renderRowLinkPath: (item) => {
      return selectedWorkspace?.is_free || selectedWorkspace?.has_subscription || !item.isLimited
        ? generatePath(RoutePaths.USER_FEEDBACK_DETAILS, {
            workspaceSlug: selectedWorkspace?.slug ?? null,
            appKey: selectedApp?.key ?? null,
            userFeedbackKey: item.key?.toString(),
          })
        : undefined;
    },
    onPreviewRequest,
    onPreviewClose,
    onDeleteRequest,
    onMarkAsReadRequest,
    total,
    selectedSortOption,
  });

  return { ...baseTableProps };
}

const columnHelper = createColumnHelper<IssueTableRowItem>();

const sortOptions = [
  { value: "key" as TicketSortOption, label: "Last created" },
  { value: "status" as TicketSortOption, label: "Status" },
  { value: "priority" as TicketSortOption, label: "Priority" },
];

export function useDefaultFeedbackColumns({
  total,
  currentMonthIssues,
  onDeleteRequest,
  onMarkAsReadRequest,
  onBulkChangePriority,
  onBulkChangeStatus,
  onBulkChangeAssignee,
  onBulkAddNoteSendChat,
  onBulkAddTags,
  onChangeStatusRequest,
  onChangePriorityRequest,
  onChangeSortOption,
  selectedSortOption,
  onChangeAssigneeRequest,
  onMergeAction,
}: Pick<
  TableProps,
  | "total"
  | "currentMonthIssues"
  | "onDeleteRequest"
  | "onMarkAsReadRequest"
  | "onBulkChangePriority"
  | "onBulkChangeStatus"
  | "onBulkChangeAssignee"
  | "onBulkAddNoteSendChat"
  | "onBulkAddTags"
  | "onChangeStatusRequest"
  | "onChangePriorityRequest"
  | "onChangeSortOption"
  | "selectedSortOption"
  | "onChangeAssigneeRequest"
  | "onMergeAction"
>) {
  return [
    columnHelper.accessor("prettyTitle", {
      cell: (info) => (
        <TicketTitleCell
          title={info.row.original.prettyTitle}
          status={info.row.original.status}
          priority={info.row.original.priority}
          ticketKey={info.row.original.key.toString()}
          linkedTicketsCount={info.row.original.linked_issues_count}
          showsIndicator={info.row.original.showNotificationIndicator}
          indicatorType={info.row.original.notificationType}
          info={info as CellContext<unknown, string>}
          onChangeStatusRequest={(status) => onChangeStatusRequest(status, info.row.original.id)}
          onChangePriorityRequest={(priority) => onChangePriorityRequest(priority, info.row.original.id)}
          table={info.table}
          onMergeAction={onMergeAction}
          onChangeAssigneeRequest={onChangeAssigneeRequest}
          isLimited={info.row.original.isLimited}
        />
      ),
      header: (info) => (
        <SelectionHeaderCell
          total={total}
          table={info.table}
          currentMonthIssues={currentMonthIssues}
          onDeleteRequest={onDeleteRequest}
          onMarkAsReadRequest={onMarkAsReadRequest}
          onBulkChangeStatus={onBulkChangeStatus}
          onBulkChangePriority={onBulkChangePriority}
          onBulkChangeAssignee={onBulkChangeAssignee}
          onBulkAddNoteSendChat={onBulkAddNoteSendChat}
          onBulkAddTags={onBulkAddTags}
          onMergeAction={onMergeAction}
        />
      ),
      size: 4, // size in fractions of available space - grid fr
    }),
    columnHelper.accessor("tags", {
      cell: (info) => (
        <BaseCell
          fadeGradientReach={100}
          style={{ display: "flex", alignItems: "center", overflow: "hidden", position: "relative" }}
        >
          {info.row.original.tags?.map((tag) => {
            return (
              <TagComponent
                key={tag.id}
                value={tag.name}
                color={getTagColor(tag.name)}
                border
              />
            );
          })}
        </BaseCell>
      ),
      header: () => <HeaderCell>Tags</HeaderCell>,
      size: 2,
    }),
    columnHelper.accessor("original", {
      cell: (info) => (
        <BaseCell
          fadeGradientReach={100}
          style={{ display: "flex", alignItems: "center", overflow: "hidden", position: "relative" }}
        >
          {info.getValue().pretty_app_version}
        </BaseCell>
      ),
      header: () => <HeaderCell>App Version</HeaderCell>,
      size: 0.7,
    }),
    columnHelper.accessor("issue_reported_time", {
      cell: (info) => (
        <BaseCell>
          <SpaceToPreviewIndicator value={info.getValue()} />
        </BaseCell>
      ),
      header: () => <HeaderCell>Created</HeaderCell>,
      size: 0.8,
    }),
    columnHelper.accessor("assignee", {
      cell: (info) => (
        <DragHandleCell
          row={info.row}
          tooltip="Drag onto another ticket to mark as its duplicate"
        />
      ),
      header: () => (
        <SortHeaderCell
          onSortRequest={onChangeSortOption}
          selectedSortOption={selectedSortOption}
          options={sortOptions}
          type="feedback"
        />
      ),
      size: 0.5,
    }),
  ];
}
